// src/services/brandStore.ts
import {
  type BrandStore,
  type BrandProfile,
  type BrandKit,
  defaultBrandStore,
  defaultBrandProfile,
  buildBrandStoreStorageKey,
  buildBrandKitStorageKey,
  migrateLegacyBrandKitToStore,
} from "../types/brand";

import { type PlanKey, canPersistBrandKit, getBrandKitsLimit } from "../config/plans";

function safeParseStore(raw: string | null): BrandStore | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as BrandStore;
    if (!parsed || parsed.version !== 1) return null;
    if (!Array.isArray(parsed.brands)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function safeParseLegacyKit(raw: string | null): BrandKit | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as BrandKit;
    if (!parsed || !Array.isArray(parsed.colors)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function clampBrandsByLimit(store: BrandStore, limit: number): BrandStore {
  if (!Number.isFinite(limit)) return store;
  if (store.brands.length <= limit) return store;

  // conserva las primeras N (orden actual) y ajusta activeBrandId
  const keep = store.brands.slice(0, limit);
  const activeOk = keep.some((b) => b.id === store.activeBrandId);
  return {
    ...store,
    brands: keep,
    activeBrandId: activeOk ? store.activeBrandId : (keep[0]?.id ?? null),
  };
}

function sanitizeForPlan(plan: PlanKey, brand: BrandProfile): BrandProfile {
  const now = Date.now();
  const base: BrandProfile = {
    ...brand,
    updatedAt: now,
    name: String(brand.name || "Mi marca"),
    description: String(brand.description || ""),
  };

  // ✅ Free: NO persistimos kit ni advanced
  if (!canPersistBrandKit(plan)) {
    const { kit, advanced, ...rest } = base as any;
    return rest as BrandProfile;
  }

  // ✅ Basic: puede kit, pero NO advanced
  if (plan === "basic") {
    const { advanced, ...rest } = base as any;
    return rest as BrandProfile;
  }

  // ✅ Pro/Premium: todo permitido
  return base;
}

export function loadBrandStore(userIdOrEmail: string): BrandStore {
  const storeKey = buildBrandStoreStorageKey(userIdOrEmail);

  // 1) Intentar store nuevo
  const existing = safeParseStore(localStorage.getItem(storeKey));
  if (existing) {
    // asegurar activeBrandId válido
    const activeOk = existing.brands.some((b) => b.id === existing.activeBrandId);
    return {
      ...existing,
      activeBrandId: activeOk ? existing.activeBrandId : (existing.brands[0]?.id ?? null),
    };
  }

  // 2) Migrar legacy BrandKit si existe
  const legacyKey = buildBrandKitStorageKey(userIdOrEmail);
  const legacyKit = safeParseLegacyKit(localStorage.getItem(legacyKey));
  if (legacyKit) {
    const migrated = migrateLegacyBrandKitToStore(legacyKit);
    localStorage.setItem(storeKey, JSON.stringify(migrated));
    // opcional: borrar legacy para evitar confusión
    localStorage.removeItem(legacyKey);
    return migrated;
  }

  // 3) Default store
  const def = defaultBrandStore();
  localStorage.setItem(storeKey, JSON.stringify(def));
  return def;
}

export function saveBrandStore(userIdOrEmail: string, store: BrandStore) {
  const storeKey = buildBrandStoreStorageKey(userIdOrEmail);
  localStorage.setItem(storeKey, JSON.stringify(store));
}

export function createBrand(
  plan: PlanKey,
  store: BrandStore,
  data?: Partial<Pick<BrandProfile, "name" | "description">>
): { ok: true; store: BrandStore } | { ok: false; reason: "limit_reached" } {
  const limit = getBrandKitsLimit(plan);

  if (Number.isFinite(limit) && store.brands.length >= limit) {
    return { ok: false, reason: "limit_reached" };
  }

  const b = defaultBrandProfile();
  const now = Date.now();

  b.name = data?.name?.trim() || b.name;
  b.description = data?.description?.trim() || "";

  b.createdAt = now;
  b.updatedAt = now;

  // ✅ aplicar gating inmediatamente
  const sanitized = sanitizeForPlan(plan, b);

  const next: BrandStore = {
    ...store,
    brands: [sanitized, ...store.brands],
    activeBrandId: sanitized.id,
  };

  return { ok: true, store: clampBrandsByLimit(next, limit) };
}

export function setActiveBrand(store: BrandStore, brandId: string): BrandStore {
  const exists = store.brands.some((b) => b.id === brandId);
  if (!exists) return store;
  return { ...store, activeBrandId: brandId };
}

export function updateActiveBrand(
  plan: PlanKey,
  store: BrandStore,
  patch: Partial<BrandProfile>
): BrandStore {
  const activeId = store.activeBrandId;
  if (!activeId) return store;

  const limit = getBrandKitsLimit(plan);

  const nextBrands = store.brands.map((b) => {
    if (b.id !== activeId) return b;

    const merged: BrandProfile = {
      ...b,
      ...patch,
      // ojo: kit y advanced pueden venir dentro de patch
      kit: patch.kit ?? b.kit,
      advanced: patch.advanced ?? b.advanced,
    };

    return sanitizeForPlan(plan, merged);
  });

  const next = { ...store, brands: nextBrands };
  return clampBrandsByLimit(next, limit);
}

export function getActiveBrand(store: BrandStore): BrandProfile | null {
  if (!store.activeBrandId) return store.brands[0] ?? null;
  return store.brands.find((b) => b.id === store.activeBrandId) ?? store.brands[0] ?? null;
}
